import { html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { msg, localized } from '@lit/localize';
import { SsoElement } from '../components/sso-base.js';
import { endpoints } from '../api/client.js';
import { navigate } from '../router.js';
import type { SignoutStep } from '../api/types.js';
import { appDisplayName } from '../app-names.js';
import '../components/sso-card.js';
import '../components/sso-button.js';
import '../components/sso-spinner.js';

type Phase = 'confirm' | 'progress' | 'done';

/**
 * `<logout-page>` — global front-channel logout (SSO-MOCKUPS.md §10).
 *
 * 10a confirm → 10b progress checklist while the browser walks the signout chain → 10c "signed out".
 * The SSO session is revoked the moment we POST /api/logout; the chain just clears the apps.
 *
 * Implementation note: a real front-channel chain navigates the top window through each app's
 * `/signout` and back. Because that would unload this SPA, we drive the chain through hidden
 * `<iframe>`s here so the checklist can render progress, then land on the done state. If an app
 * blocks framing, we still consider the SSO session revoked (the authoritative step).
 */
@customElement('logout-page')
@localized()
export class LogoutPage extends SsoElement {
  static override styles = [
    SsoElement.styles,
    css`
      .actions {
        display: flex;
        gap: var(--sso-space);
      }
      .actions sso-button {
        flex: 1;
      }
      ul.chain {
        list-style: none;
        padding: 0;
        margin: var(--sso-space) 0;
      }
      ul.chain li {
        display: flex;
        gap: 0.6rem;
        align-items: center;
        padding: 0.35rem 0;
        color: var(--sso-color-fg);
      }
      .state {
        width: 1.3rem;
        text-align: center;
      }
      .pending {
        color: var(--sso-color-muted);
      }
      .doing {
        color: var(--sso-color-brand);
      }
      .ok {
        color: var(--sso-color-success);
      }
      .center {
        text-align: center;
      }
      .frames {
        display: none;
      }
      .success-badge {
        font-size: 2.5rem;
        text-align: center;
        color: var(--sso-color-success);
      }
    `,
  ];

  @state() private phase: Phase = 'confirm';
  @state() private busy = false;
  @state() private chain: { label: string; url: string; status: 'pending' | 'doing' | 'ok' }[] = [];

  override firstUpdated(): void {
    // Focus the first action (Cancel, the safe default) rather than the heading text.
    this.focusFirstControl();
  }

  /** Friendly display name per app; falls back to the URL's host for an unknown value. */
  private labelFor(step: SignoutStep): string {
    const name = appDisplayName(step.app);
    if (name) return name;
    try {
      return new URL(step.url).host;
    } catch {
      return step.url;
    }
  }

  private async confirm(): Promise<void> {
    this.busy = true;
    // Drop any transient login-flow state (notably a prior login's stashed returnUri) so it can't
    // leak into the next user's session on a shared terminal — logout is a soft client navigation,
    // so the in-memory FlowContext on <sso-app> would otherwise survive into the next sign-in.
    this.flowContext?.reset();
    try {
      const r = await endpoints.logout();
      this.chain = (r.signoutChain ?? []).map((s) => ({
        label: this.labelFor(s),
        url: s.url,
        status: 'pending' as const,
      }));
      this.phase = 'progress';
      await this.runChain();
    } catch {
      // Even on error the SSO session is considered revoked; show the done state.
      this.phase = 'done';
    } finally {
      this.busy = false;
    }
  }

  /** Walk each app signout URL via a hidden iframe so the checklist can show progress. */
  private async runChain(): Promise<void> {
    for (let i = 0; i < this.chain.length; i++) {
      this.chain = this.chain.map((c, k) => (k === i ? { ...c, status: 'doing' } : c));
      await this.pingSignout(this.chain[i].url);
      this.chain = this.chain.map((c, k) => (k === i ? { ...c, status: 'ok' } : c));
    }
    this.phase = 'done';
  }

  /** Load an app's /signout in a hidden iframe; resolve on load or after a short timeout. */
  private pingSignout(url: string): Promise<void> {
    return new Promise((resolve) => {
      const frame = document.createElement('iframe');
      frame.setAttribute('aria-hidden', 'true');
      frame.style.display = 'none';
      const cleanup = (): void => {
        window.clearTimeout(timer);
        frame.remove();
        resolve();
      };
      const timer = window.setTimeout(cleanup, 4000); // don't hang on a framing-blocked app
      frame.addEventListener('load', cleanup);
      frame.addEventListener('error', cleanup);
      frame.src = url;
      this.renderRoot.querySelector('.frames')?.appendChild(frame);
    });
  }

  private cancel(): void {
    navigate('/');
  }

  private renderConfirm() {
    return html`
      <h1>${msg('Sign out')}</h1>
      <p>
        ${msg(
          'You will be signed out of PinakesSSO and of all open applications (CRM, Export, Callimachus) in this browser.',
        )}
      </p>
      <div class="actions">
        <sso-button variant="secondary" ?disabled=${this.busy} @click=${this.cancel}>
          ${msg('Cancel')}
        </sso-button>
        <sso-button variant="primary" ?busy=${this.busy} @click=${this.confirm}>
          ${msg('Sign out')}
        </sso-button>
      </div>
    `;
  }

  private renderProgress() {
    const sym = (s: string): string => (s === 'ok' ? '✓' : s === 'doing' ? '◴' : '·');
    return html`
      <h1>${msg('Signing out…')}</h1>
      <p>${msg('Signing out of applications')}</p>
      <ul class="chain">
        ${this.chain.map(
          (c) => html`<li>
            <span class="state ${c.status}" aria-hidden="true">${sym(c.status)}</span>
            ${c.label}
          </li>`,
        )}
      </ul>
      <p class="center">${msg('One moment please')}</p>
      <div class="frames"></div>
    `;
  }

  private renderDone() {
    return html`
      <div class="success-badge" aria-hidden="true">✓</div>
      <h1 class="center">${msg('You are signed out')}</h1>
      <p class="center">${msg('You have been securely signed out of all Pinakes applications.')}</p>
      <sso-button variant="primary" @click=${() => navigate('/login')}>
        ${msg('Sign in again')}
      </sso-button>
    `;
  }

  override render() {
    return html`
      <sso-card>
        ${this.phase === 'confirm'
          ? this.renderConfirm()
          : this.phase === 'progress'
            ? this.renderProgress()
            : this.renderDone()}
      </sso-card>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'logout-page': LogoutPage;
  }
}
